import type { PullRequestTarget } from '@/data/v2/pr/approve-pull-request';
import type { NavigateToPath } from '@/lib/desktop-app-navigation';
import type { PrV2MergeResponse } from '@/lib/pr-v2/types';
import type { notification } from '@todesktop/client-core';

import type { ToastContent } from './send-notifications';

import { mergeFromNotification } from './merge-from-notification';
import { getInternalNotificationPath } from './notification-path';
import { replyFromNotification } from './perform-desktop-quick-action';
import { closeToastsAbout } from './pull-request-toasts';

import { approvePullRequest } from '@/data/v2/pr/approve-pull-request';
import { planPullRequestMerge } from '@/data/v2/pr/plan-pull-request-merge';
import { clientLogger } from '@/lib/client-logger';
import { openInDesktopAppWindow } from '@/lib/desktop-app-navigation';

type NotificationOptions = Pick<
  Parameters<typeof notification.create>[0],
  'actions' | 'hasReply' | 'replyPlaceholder'
>;

/** A notification's quick action, ready to show: its text, its controls, and what they do. */
export type PreparedQuickAction = {
  body: string;
  options: NotificationOptions;
  /** Runs when the second button is clicked; the first, `Open`, opens the notification's link. */
  onButton: (() => void) | null;
  /** Runs with the trimmed, non-empty text of an inline reply. */
  onReply: ((text: string) => void) | null;
};

type QuickActionContext = {
  navigate: NavigateToPath;
  /** Shows a follow-up notification confirming how a quick action went. */
  notify: (followUp: ToastContent) => Promise<unknown>;
};

const NO_CONTROLS: NotificationOptions = { hasReply: false };

const MERGE_FOLLOW_UP_TITLES: Record<PrV2MergeResponse['status'], (pr: string) => string> = {
  merged: (pr) => `Merged ${pr}`,
  enqueued: (pr) => `Added ${pr} to the merge queue`,
  pending: (pr) => `GitHub is still merging ${pr}`,
};

function withButton(text: string): NotificationOptions {
  // `Open` stays first so it is the default.
  return {
    actions: [
      { type: 'button', text: 'Open' },
      { type: 'button', text },
    ],
    hasReply: false,
  };
}

function describePullRequest({ owner, repo, number }: PullRequestTarget): string {
  return `${owner}/${repo} #${number}`;
}

/** Runs a quick action and confirms the result with a follow-up notification. */
function runWithFollowUp<T>(
  { notify }: QuickActionContext,
  link: string,
  perform: () => Promise<T>,
  outcome: {
    successTitle: (result: T) => string;
    failureTitle: string;
    failureBody: (error: unknown) => string;
  },
): void {
  const followUp = (title: string, body: string) =>
    notify({ title, body, link, quickAction: null, desktopOnly: true });

  void perform().then(
    (result) => {
      closeToastsAbout(link);
      return followUp(outcome.successTitle(result), '');
    },
    (error: unknown) => {
      clientLogger.error('Desktop notification quick action failed', error);
      return followUp(outcome.failureTitle, outcome.failureBody(error));
    },
  );
}

/**
 * Turns a notification's quick action into what the desktop app shows. An approval offers Merge
 * only when the viewer can merge it right now; otherwise it shows as a plain notification, and
 * clicking Merge checks again.
 */
export async function prepareQuickAction(
  { body, link, quickAction }: ToastContent,
  context: QuickActionContext,
): Promise<PreparedQuickAction> {
  const plain: PreparedQuickAction = {
    body,
    options: NO_CONTROLS,
    onButton: null,
    onReply: null,
  };

  switch (quickAction?.kind) {
    case undefined:
      return plain;
    case 'approve': {
      // One ID for the button, so a double click cannot approve twice.
      const submissionId = crypto.randomUUID();
      return {
        ...plain,
        options: withButton('Approve'),
        onButton: () =>
          runWithFollowUp(context, link, () => approvePullRequest(quickAction, submissionId), {
            successTitle: () => `Approved ${describePullRequest(quickAction)}`,
            failureTitle: `Couldn't approve ${describePullRequest(quickAction)}`,
            failureBody: () => 'Click to open the pull request.',
          }),
      };
    }
    case 'merge': {
      const plan = await planPullRequestMerge(quickAction).catch((error: unknown) => {
        clientLogger.error('Failed to check whether a pull request can be merged', error);
        return null;
      });
      if (!plan?.ready) {
        return plain;
      }
      return {
        ...plain,
        body: [body, 'Checks passed · ready to merge'].filter(Boolean).join('\n'),
        options: withButton(plan.merge.merge_action === 'queue' ? 'Add to merge queue' : 'Merge'),
        onButton: () =>
          runWithFollowUp(context, link, () => mergeFromNotification(quickAction), {
            successTitle: (result) =>
              MERGE_FOLLOW_UP_TITLES[result.status](describePullRequest(quickAction)),
            failureTitle: `Couldn't merge ${describePullRequest(quickAction)}`,
            // GitHub and the readiness check both explain what blocks the merge.
            failureBody: (error) =>
              error instanceof Error && error.message
                ? `${error.message}\nClick to open the pull request.`
                : 'Click to open the pull request.',
          }),
      };
    }
    case 'open': {
      const path = getInternalNotificationPath(quickAction.path);
      return {
        ...plain,
        options: withButton(quickAction.label),
        onButton: () =>
          void openInDesktopAppWindow(path, context.navigate).catch((error) => {
            clientLogger.error('Failed to open a desktop notification button target', error);
          }),
      };
    }
    case 'reply':
      return {
        ...plain,
        options: { hasReply: true, replyPlaceholder: 'Reply…' },
        onReply: (text) =>
          runWithFollowUp(context, link, () => replyFromNotification(quickAction, text), {
            successTitle: () => `Replied on ${describePullRequest(quickAction)}`,
            failureTitle: `Couldn't post your reply on ${describePullRequest(quickAction)}`,
            // Keep the text visible so a failed reply is not lost.
            failureBody: () => `Click to open the comment. Your reply: ${text}`,
          }),
      };
  }
}
